import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    const currentVariantId = this.#getCurrentVariantId();
    this.#filterSockelleisteVariantMedia(this, currentVariantId);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Gets the currently selected variant ID from the gallery, URL or variant picker JSON.
   *
   * @returns {string | null}
   */
  #getCurrentVariantId() {
    if (this.dataset.variantId) return this.dataset.variantId;

    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    if (urlVariantId) return urlVariantId;

    const variantJson = this.closest('.shopify-section')?.querySelector('variant-picker script[type="application/json"]')
      ?.textContent;

    if (!variantJson) return null;

    try {
      return String(JSON.parse(variantJson).id ?? '');
    } catch {
      return null;
    }
  }

  /**
   * Keeps the sockelleiste galleries variant-specific.
   * For each size, only the matching images remain in every gallery view
   * (main slideshow/grid, thumbnails and zoom gallery).
   *
   * @param {Element} gallery - Media gallery to process.
   * @param {string | number | null | undefined} variantId - Currently selected variant ID.
   */
  #filterSockelleisteVariantMedia(gallery, variantId) {
    const isSockelleistePage = window.location.pathname.includes(
      '/products/kiefer-sockelleiste-ral-9016-verkehrsweiss-lackiert'
    );

    if (!isSockelleistePage) return;

    const selectedVariantId = String(variantId ?? '').split('/').pop();
    const variantImageFilenames = {
      '58633159541110': [
        'DSC_0025.jpg',
        'kiefer-sockelleiste-ral9016-titel.jpg',
        'kiefer-sockelleiste-ral9016-2146.jpg',
        'kiefer-sockelleiste-ral9016-2149.jpg',
        'kiefer-sockelleiste-ral9016-2150.jpg',
        'kiefer-sockelleiste-ral9016-2151.jpg',
      ],
      '58633159573878': [
        'kiefer-sockelleiste-58x15-dsc0003.jpg',
        'kiefer-sockelleiste-58x15-img2521.jpg',
      ],
      '58633159606646': [
        'kiefer-sockelleiste-80x15-2157.jpg',
        'kiefer-sockelleiste-80x15-2166.jpg',
      ],
    };
    const selectedVariantImageFilenames = variantImageFilenames[selectedVariantId];
    const secondaryVariantImageFilenames = [
      'kiefer-sockelleiste-58x15-img2521.jpg',
      'kiefer-sockelleiste-80x15-2166.jpg',
    ];

    const mediaItemSelector = [
      'slideshow-slide.product-media-container',
      '.media-gallery__grid > li.product-media-container',
      '.dialog-zoomed-gallery > li.product-media-container',
      '.slideshow-controls__thumbnail',
      '.dialog-thumbnails-list__thumbnail',
    ].join(', ');

    gallery.querySelectorAll(mediaItemSelector).forEach((mediaItem) => {
      const images = Array.from(mediaItem.querySelectorAll('img'));
      const imageSources = images
        .flatMap((image) => [image.currentSrc, image.src, image.getAttribute('src'), image.getAttribute('srcset')])
        .filter(Boolean)
        .join(' ');

      if (selectedVariantImageFilenames) {
        const isAllowedVariantImage = selectedVariantImageFilenames.some((filename) => imageSources.includes(filename));
        if (!isAllowedVariantImage) mediaItem.remove();
        return;
      }

      const isSecondaryVariantImage = secondaryVariantImageFilenames.some((filename) => imageSources.includes(filename));
      if (isSecondaryVariantImage) mediaItem.remove();
    });
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const variantId = event.detail.resource?.id;
    if (variantId != null) {
      newMediaGallery.dataset.variantId = String(variantId);
    }
    this.#filterSockelleisteVariantMedia(newMediaGallery, variantId);

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the image to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
